import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line no-duplicate-imports
import type { tenantType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import type { TrackEventPayload } from '@atlassiansox/analytics-web-client/types';

import type { GasPurePayload, GasPureScreenEventPayload } from '@atlaskit/analytics-gas-types';
import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import type { AnalyticsWebClient as AnalyticsWebClientListener } from '@atlaskit/analytics-listeners/types';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import {
	ANALYTICS_BRIDGE_CHANNEL,
	OPERATIONAL_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	UI_EVENT_TYPE,
} from '@atlassian/analytics-bridge/constants';
import { extractAWCDataFromEvent } from '@atlassian/analytics-bridge/extract-awc-data-from-event';
import { initialiseErrorAnalyticsClient } from '@atlassian/error-handling/fire-error-analytics';

import { ReliabilityTrackerSubscribers } from '../../reliability-tracker/subscribers';

type CustomAttributes = { [key: string]: any };

interface AnalyticsContextValue {
	analyticsClient: AnalyticsWebClient;
	containerId: string | undefined;
	customAttributes: CustomAttributes;
	setContainerId: (id: string | undefined) => void;
	updateCustomAttributes: (key: string, value: any) => void;
}

export const AnalyticsContext: React.Context<AnalyticsContextValue | null> =
	createContext<AnalyticsContextValue | null>(null);

/**
 * An adapter layer to translate between "@atlaskit/analytics-listeners" and
 * "@atlassiansox/analytics-web-client" due to discrepancy between the defined types
 */
const useAtlasKitAnalytics = (
	analyticsWebClient: AnalyticsWebClient,
	containerId: string | undefined,
): AnalyticsWebClientListener => {
	return useMemo(() => {
		const containerContext =
			containerId !== undefined ? { containerType: 'spaceId' as const, containerId } : {};
		return {
			sendUIEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendUIEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
					...containerContext,
				});
			},
			sendOperationalEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendOperationalEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
					...containerContext,
				});
			},
			sendTrackEvent: (event: GasPurePayload) => {
				return analyticsWebClient.sendTrackEvent({
					...event,
					action: event.action || '',
					source: event.source || 'atlassian-studio',
					...containerContext,
				});
			},
			sendScreenEvent: (event: GasPureScreenEventPayload) => {
				return analyticsWebClient.sendScreenEvent(event);
			},
		};
	}, [analyticsWebClient, containerId]);
};

export type AnalyticsProviderProps = {
	analyticsClient: AnalyticsWebClient;
	children: React.ReactNode;
};

export const AnalyticsProvider = ({
	children,
	analyticsClient,
}: AnalyticsProviderProps): React.JSX.Element => {
	const [containerId, setContainerId] = useState<string | undefined>(undefined);
	const [customAttributes, setCustomAttributes] = useState<CustomAttributes>({});

	const updateCustomAttributes = useCallback((key: string, value: any) => {
		setCustomAttributes((prevConfig) => ({ ...prevConfig, [key]: value }));
	}, []);

	const sendEvent = useCallback(
		(event: any) => {
			const { type, payload } = event;
			const payloadWithSource = { source: 'atlassian-studio', ...payload };

			switch (type) {
				case UI_EVENT_TYPE:
					analyticsClient.sendUIEvent(payloadWithSource);
					break;
				case TRACK_EVENT_TYPE:
					analyticsClient.sendTrackEvent(payloadWithSource);
					break;
				case OPERATIONAL_EVENT_TYPE:
					analyticsClient.sendOperationalEvent(payloadWithSource);
					break;
				case SCREEN_EVENT_TYPE:
					analyticsClient.sendScreenEvent(payload.name, null, payload.attributes);
					break;
				default:
					break;
			}
		},
		[analyticsClient],
	);

	useEffect(() => {
		initialiseErrorAnalyticsClient({
			sendOperationalEvent: (event: any) => {
				analyticsClient.sendOperationalEvent({
					action: event.action || 'unknown',
					actionSubject: event.actionSubject || 'error',
					source: event.source || 'atlassian-studio',
					...event,
				});
			},
		});
	}, [analyticsClient]);

	return (
		<AnalyticsContext.Provider
			value={{
				analyticsClient,
				containerId,
				customAttributes,
				setContainerId,
				updateCustomAttributes,
			}}
		>
			<AnalyticsListener
				channel={ANALYTICS_BRIDGE_CHANNEL}
				onEvent={(event) => sendEvent(extractAWCDataFromEvent(event))}
			>
				<FabricAnalyticsListeners client={useAtlasKitAnalytics(analyticsClient, containerId)}>
					<ReliabilityTrackerSubscribers />
					{children}
				</FabricAnalyticsListeners>
			</AnalyticsListener>
		</AnalyticsContext.Provider>
	);
};

export const useAnalytics = (): {
	containerId: string | undefined;
	customAttributes: CustomAttributes;
	sendTrackEvent: (event: TrackEventPayload, callback?: any) => Promise<void>;
	setContainerId: (id: string | undefined) => void;
	setTenantInfo: (tenantIdType: tenantType, tenantId?: string) => void;
	setUserInfo: (userIdType: string, userId: string) => void;
	updateCustomAttributes: (key: string, value: any) => void;
} => {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error('useAnalytics must be used within an AnalyticsProvider');
	}

	const { containerId, customAttributes, setContainerId, updateCustomAttributes, analyticsClient } =
		context;

	const { setTenantInfo, setUserInfo, sendTrackEvent } = analyticsClient;

	return {
		containerId,
		customAttributes,
		setContainerId,
		setTenantInfo,
		setUserInfo,
		updateCustomAttributes,
		sendTrackEvent,
	};
};
